import { routePaths } from "../constants/routes";

export const apiClusterInternalLinks = [
  { label: "Learn Automation Testing", to: "/automation-testing-tutorial" },
  { label: "Learn Manual Testing", to: routePaths.manualTesting },
  { label: "Testing Interview Questions", to: routePaths.interviewQuestions },
];

const defaultAdvantages = [
  "Finds backend defects before they appear in the UI.",
  "Validates business rules, response data, authentication, and integrations directly.",
  "Runs faster than many UI tests and gives quick feedback in CI/CD pipelines.",
  "Helps testers understand real application architecture, services, and data flow.",
];

const defaultDisadvantages = [
  "Beginners may need time to understand HTTP, JSON, headers, tokens, and status codes.",
  "APIs can be hard to test when documentation, test data, or environments are unstable.",
  "API tests do not replace UI testing because they cannot verify layout, usability, or visual behavior.",
];

function faqFor(keyword) {
  return [
    {
      question: `Is ${keyword} important for QA testers?`,
      answer: `Yes. ${keyword} is important because many modern applications use APIs for login, payments, user data, reports, mobile apps, and third-party integrations.`,
    },
    {
      question: `Do I need coding to learn ${keyword}?`,
      answer: "No for basic API testing in Postman. Coding becomes useful later when you automate API tests using tools such as Rest Assured, Playwright API, Cypress API, or JavaScript libraries.",
    },
    {
      question: `How should I practice ${keyword}?`,
      answer: "Practice GET, POST, PUT, PATCH, and DELETE requests. Validate status codes, response body, headers, schema, authentication, negative cases, and response time.",
    },
  ];
}

function makeApiPage({
  slug,
  title,
  description,
  keyword,
  what,
  why,
  types,
  example,
  questions,
}) {
  return {
    slug,
    path: `/${slug}`,
    eyebrow: "API Testing SEO Cluster",
    title,
    description,
    keyword,
    sections: [
      {
        id: "what-is-it",
        heading: `What is ${keyword}?`,
        paragraphs: what,
      },
      {
        id: "why-important",
        heading: "Why important",
        paragraphs: why,
      },
      {
        id: "types",
        heading: "Types",
        list: types,
      },
      {
        id: "example",
        heading: "Example",
        paragraphs: example,
      },
      {
        id: "advantages",
        heading: "Advantages",
        list: defaultAdvantages,
      },
      {
        id: "disadvantages",
        heading: "Disadvantages",
        list: defaultDisadvantages,
      },
      {
        id: "interview-questions",
        heading: "Interview Questions",
        list: questions,
      },
      {
        id: "faq",
        heading: "FAQ",
        faqs: faqFor(keyword),
      },
    ],
  };
}

export const apiTestingClusterPages = [
  makeApiPage({
    slug: "api-testing-tutorial",
    title: "API Testing Tutorial for Beginners",
    description: "Learn API testing step by step with HTTP methods, status codes, headers, payloads, authentication, Postman examples, test cases, and FAQs.",
    keyword: "API Testing Tutorial",
    what: [
      "API testing is the process of validating application programming interfaces directly by sending requests and checking responses. A tester verifies whether the API returns the correct status code, response body, headers, schema, and business result.",
      "An API testing tutorial starts with HTTP basics, then moves into endpoints, methods, parameters, request body, authentication, Postman collections, assertions, negative testing, and automation.",
    ],
    why: [
      "API testing is important because APIs connect frontend screens, mobile apps, databases, microservices, payment gateways, and third-party systems.",
      "Testing APIs early helps teams catch backend defects before UI testing and gives faster feedback for releases.",
    ],
    types: ["Functional API testing", "Negative API testing", "Contract testing", "Security testing", "Performance testing", "API automation"],
    example: [
      "Example: For a login API, send a POST request with email and password. Validate that valid credentials return 200 OK and a token, while invalid credentials return 401 Unauthorized with a clear error message.",
      "A tester should also verify missing fields, invalid email format, blocked user, expired token, and response time.",
    ],
    questions: ["What is API testing?", "Why is API testing important?", "What are HTTP methods?", "What is the difference between 200 and 201?", "How do you test authentication APIs?"],
  }),
  makeApiPage({
    slug: "postman-tutorial",
    title: "Postman Tutorial for API Testing",
    description: "Learn Postman for API testing with collections, environments, variables, headers, authorization, body data, tests, runners, and practical examples.",
    keyword: "Postman Tutorial",
    what: [
      "Postman is an API testing tool used to send requests, inspect responses, manage collections, add authorization, create variables, and write simple test scripts.",
      "A Postman tutorial helps beginners practice APIs without writing code first. It is useful for manual testers, API testers, and automation learners.",
    ],
    why: [
      "Postman is important because it gives testers a visual and practical way to understand how APIs work.",
      "It also helps teams share API collections, run smoke checks, validate response data, and document API behavior.",
    ],
    types: ["Collections", "Environments", "Variables", "Authorization", "Pre-request scripts", "Test scripts", "Collection runner"],
    example: [
      "Example: Create a Postman request for GET /users, add the base URL as an environment variable, send the request, verify status code 200, and check that the response contains a user list.",
      "For login, save the token from the response into a variable and reuse it in the Authorization header for protected APIs.",
    ],
    questions: ["What is Postman?", "What is a Postman collection?", "What are environment variables?", "How do you pass a bearer token?", "How do you validate status code in Postman?"],
  }),
  makeApiPage({
    slug: "rest-api-testing",
    title: "REST API Testing Guide",
    description: "Learn REST API testing with resources, endpoints, methods, headers, query parameters, payloads, authentication, status codes, and real examples.",
    keyword: "REST API Testing",
    what: [
      "REST API testing validates APIs that follow REST principles such as resources, stateless requests, HTTP methods, and standard response formats like JSON.",
      "A REST API tester checks endpoints, methods, request parameters, request body, authentication, response data, status codes, headers, and performance.",
    ],
    why: [
      "REST APIs are used in most modern web and mobile applications, so QA testers must understand how to validate them.",
      "Good REST API testing catches data, permission, integration, and business rule defects before users experience them in the UI.",
    ],
    types: ["GET testing", "POST testing", "PUT testing", "PATCH testing", "DELETE testing", "Authentication testing", "Schema validation"],
    example: [
      "Example: For GET /orders/{id}, verify that a valid order ID returns 200 OK and correct order data. Invalid ID should return 404 Not Found, and an unauthorized user should receive 401 or 403 based on the rule.",
    ],
    questions: ["What is REST API testing?", "GET vs POST?", "PUT vs PATCH?", "What is stateless communication?", "How do you validate JSON response?"],
  }),
  makeApiPage({
    slug: "api-testing-interview-questions",
    title: "API Testing Interview Questions and Answers",
    description: "Prepare API testing interview questions on HTTP methods, status codes, Postman, authentication, payloads, headers, schema validation, and scenarios.",
    keyword: "API Testing Interview Questions",
    what: [
      "API testing interview questions check whether a tester understands backend validation, HTTP methods, status codes, authentication, request-response structure, and practical test scenarios.",
      "Good answers should include the definition, example request, expected response, negative cases, and how the issue would be reported.",
    ],
    why: [
      "API testing is a common interview topic because companies need testers who can validate backend behavior even when the UI is not ready.",
      "It also shows whether a candidate can think beyond screens and understand data flow between services.",
    ],
    types: ["Definition questions", "Method questions", "Status code questions", "Authentication questions", "Postman questions", "Scenario questions"],
    example: [
      "Example answer: 401 Unauthorized means authentication is missing or invalid. 403 Forbidden means authentication may be valid, but the user does not have permission to access the resource.",
    ],
    questions: ["What is API testing?", "What is Postman?", "Difference between 401 and 403?", "GET vs POST?", "What is bearer token?", "How do you test a login API?", "What is schema validation?"],
  }),
  makeApiPage({
    slug: "api-status-codes",
    title: "API Status Codes for Testers",
    description: "Learn important HTTP API status codes including 200, 201, 204, 400, 401, 403, 404, 409, 422, 500, and practical testing examples.",
    keyword: "API Status Codes",
    what: [
      "API status codes are numeric HTTP response codes that tell the client whether a request succeeded, failed, needs authentication, has invalid data, or caused a server error.",
      "For API testers, status codes are one of the first checks in every request because they show whether the API behavior matches the expected contract.",
    ],
    why: [
      "Status codes are important because they make API behavior clear and predictable for frontend, mobile, and third-party clients.",
      "Incorrect status codes can confuse applications, hide defects, or make error handling unreliable.",
    ],
    types: ["2xx success codes", "3xx redirect codes", "4xx client error codes", "5xx server error codes", "Authentication and authorization codes"],
    example: [
      "Example: POST /users should return 201 Created when a user is created successfully. If the email already exists, the API may return 409 Conflict. If the email format is invalid, it may return 400 Bad Request or 422 Unprocessable Entity.",
    ],
    questions: ["What does 200 mean?", "Difference between 201 and 204?", "401 vs 403?", "When should API return 404?", "What does 500 Internal Server Error mean?"],
  }),
  makeApiPage({
    slug: "api-testing-examples",
    title: "API Testing Examples with Real-Time Scenarios",
    description: "Practice API testing examples for login, signup, user profile, product search, cart, orders, payments, authentication, and negative cases.",
    keyword: "API Testing Examples",
    what: [
      "API testing examples are practical scenarios used to learn how backend features should be validated. These examples help testers move from theory to real project thinking.",
      "Common API examples include login, signup, user profile, product search, cart, order creation, payment validation, file upload, and report download.",
    ],
    why: [
      "Real API examples are important because interviews and projects often ask how you would test a specific endpoint.",
      "Examples teach testers to check positive cases, negative cases, boundary data, authorization, response schema, and database impact.",
    ],
    types: ["Login API examples", "Signup API examples", "Search API examples", "Cart API examples", "Payment API examples", "Authorization examples"],
    example: [
      "Example: For cart API, test adding a valid product, invalid product ID, quantity zero, quantity more than stock, duplicate product, unauthorized user, and response total calculation.",
      "For search API, test keyword search, empty keyword, special characters, pagination, sorting, filter combinations, and response time.",
    ],
    questions: ["Give API testing examples.", "How do you test login API?", "How do you test cart API?", "What negative cases can you write for APIs?", "How do you validate response data?"],
  }),
  makeApiPage({
    slug: "soap-vs-rest-api",
    title: "SOAP vs REST API Testing",
    description: "Compare SOAP and REST API testing with protocols, payload format, use cases, tools, advantages, disadvantages, and interview questions.",
    keyword: "SOAP vs REST API",
    what: [
      "SOAP and REST are two approaches used to build APIs. SOAP is a protocol that usually uses XML and strict contracts. REST is an architectural style that commonly uses HTTP methods and JSON.",
      "In testing, SOAP often requires validating WSDL, XML body, SOAP actions, and strict schemas. REST testing focuses on endpoints, methods, status codes, JSON body, headers, and authentication.",
    ],
    why: [
      "This comparison is important because interviewers often ask testers to explain SOAP vs REST and when each one is used.",
      "Understanding both helps testers work with legacy enterprise systems as well as modern web and mobile APIs.",
    ],
    types: ["SOAP API testing", "REST API testing", "XML validation", "JSON validation", "Contract testing", "Security testing"],
    example: [
      "Example: A banking system may expose SOAP services for strict enterprise integrations, while a mobile shopping app may use REST APIs for product search, cart, and checkout.",
    ],
    questions: ["SOAP vs REST?", "Which is faster SOAP or REST?", "What is WSDL?", "Why is REST popular?", "Can Postman test SOAP APIs?"],
  }),
  makeApiPage({
    slug: "api-testing-tools",
    title: "API Testing Tools List",
    description: "Compare API testing tools including Postman, Rest Assured, SoapUI, Swagger, Playwright API, Cypress API, JMeter, and Newman.",
    keyword: "API Testing Tools",
    what: [
      "API testing tools help testers send requests, validate responses, automate checks, generate reports, and run API tests in pipelines.",
      "Tool selection depends on whether the team needs manual exploration, automation, contract validation, performance checks, or CI/CD execution.",
    ],
    why: [
      "API testing tools are important because they make backend validation faster, repeatable, and easier to share across QA and development teams.",
      "They also help testers create regression suites for services that do not depend on UI screens.",
    ],
    types: ["Postman", "Rest Assured", "SoapUI", "Swagger", "Newman", "Playwright API", "Cypress API", "JMeter"],
    example: [
      "Example: Use Postman for manual API exploration, Newman to run Postman collections in CI, Rest Assured for Java API automation, SoapUI for SOAP services, and JMeter for API performance testing.",
    ],
    questions: ["Which API testing tool is best?", "Postman vs Rest Assured?", "What is Newman?", "Can JMeter test APIs?", "What is Swagger used for?"],
  }),
  makeApiPage({
    slug: "api-test-cases",
    title: "API Test Cases with Examples",
    description: "Learn how to write API test cases for status code, response body, headers, schema, authentication, authorization, negative cases, and performance.",
    keyword: "API Test Cases",
    what: [
      "API test cases are documented checks that validate API behavior for specific inputs and expected outputs. They cover request method, endpoint, headers, payload, expected status code, expected response, and validations.",
      "Good API test cases include positive, negative, boundary, authentication, authorization, schema, and performance checks.",
    ],
    why: [
      "API test cases are important because they make backend testing structured, repeatable, and reviewable.",
      "They also help automation engineers convert manual API scenarios into automated regression suites.",
    ],
    types: ["Positive API cases", "Negative API cases", "Boundary cases", "Authentication cases", "Authorization cases", "Schema validation cases", "Performance cases"],
    example: [
      "Example test case: Verify login API with valid credentials. Method: POST. Endpoint: /login. Body: valid email and password. Expected status: 200 OK. Expected response: token and user profile data.",
      "Negative case: Verify login API with wrong password. Expected status: 401 Unauthorized and clear error message.",
    ],
    questions: ["What is an API test case?", "What fields are included in API test cases?", "How do you write negative API test cases?", "How do you test authorization?", "What is schema validation?"],
  }),
];

export function getApiTestingClusterPage(slug) {
  return apiTestingClusterPages.find((page) => page.slug === slug);
}
